/**
 * useGlobalRealtime — AppShell-level WebSocket subscription.
 *
 * Mounted once in `<AppShell>` so it's alive on every authenticated route
 * (home, board, timesheet, project settings). Handles the user-scoped
 * frames whose effects must update UI outside the board:
 *
 *   - `notification.created` → invalidate ['notifications'] so the bell
 *     badge ticks even when the user is on /timesheet or /home.
 *   - `timer.started` / `timer.stopped` → invalidate ['timer','running']
 *     and ['time-entries'] so the AppShell pill flips state instantly.
 *     `timer.stopped` with `reason: 'cap'` surfaces the 8h-cap info toast.
 *
 * The board's own `useRealtimeBoard` still handles `card.*` and
 * `comment.*` frames (project-scoped), and ALSO continues to dispatch the
 * notification + timer frames as a defensive backup. There's no harm in
 * the same query being invalidated twice — TanStack Query coalesces.
 *
 * The hook IS NOT project-scoped: it runs whenever a Cognito access
 * token exists. If the WebSocket URL isn't configured (`VITE_WS_URL`
 * unset), it noops.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clearTokens } from '@/auth/cookie-helpers'
import { useToast } from '@/components/ToastProvider'
import { agentWorkingStore } from '@/features/agents/agent-working-store'
import { CARD_LIST_QUERY_ROOT } from '@/features/board/components/list/use-card-list'
import { invalidateCardActivityFromFrame } from '@/features/card-activity/realtime'
import { handleCodeFrame } from '@/features/code/realtime'
import { DASHBOARD_QUERY_KEY } from '@/features/dashboard/use-dashboard'
import { NOTIFICATIONS_QUERY_KEY } from '@/features/notifications/use-notifications'
import { applyPresenceOffline, applyPresenceOnline } from '@/features/presence/use-presence'
import { invalidateAgentRunSurfaces } from '@/features/tenant-settings/agents/use-agent-runs'
import { AVAILABLE_WORK_QUERY_ROOT } from '@/features/time/use-available-work'
import { invalidateTimerDerivedCaches } from '@/features/time/use-running-timer'
import {
  WIKI_TREE_QUERY_KEY,
  wikiDocAclQueryKey,
  wikiDocPrefix,
} from '@/features/wiki/hooks/query-keys'
import { shortIdTail } from '@/features/wiki/lib/slug'

// Wiki doc URLs come in two shapes: legacy `/wiki/d/{uuid}` (still routed
// for back-compat + folder kinds + id-only callers) and Notion-style
// `/wiki/{slug}-{8charTail}` (the canonical form for docs post-Wave 3).
// Realtime deep-link checks have to match both — otherwise users on the
// pretty URL silently miss delete-redirects + cross-user draft toasts.
const isOnWikiDocPath = (pathname: string, docId: string): boolean =>
  pathname.endsWith(`/wiki/d/${docId}`) || pathname.endsWith(`-${shortIdTail(docId)}`)
import type { ServerMessage, WebSocketStatus } from '@/lib/use-websocket'
import { useWebSocketSubscription } from '@/lib/use-websocket-subscription'

export interface UseGlobalRealtimeResult {
  wsStatus: WebSocketStatus
}

export function useGlobalRealtime(): UseGlobalRealtimeResult {
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['notifications', 'wiki'])

  // Token freshness + WS lifecycle live on `<WebSocketProvider>` now —
  // this hook just multiplexes the shared connection. The provider holds
  // `useFreshAccessToken` so token rotation reaches this consumer
  // through a single underlying reconnect, not N parallel ones.

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      const { type } = message

      // User-scoped revocation. The BE deactivate handler broadcasts
      // this frame to every connected tab for the affected user. Clear
      // tokens + bounce to /login. Defense-in-depth: even if this
      // frame doesn't arrive (network blip, user not currently
      // connected), the BE's middleware-level deactivation check
      // catches the next API call with 401 and apiClient's 401-chain
      // does the same bounce.
      if (type === 'auth.deactivated') {
        const payload =
          typeof message.payload === 'object' && message.payload !== null
            ? (message.payload as Record<string, unknown>)
            : null
        const reason =
          (payload && typeof payload.message === 'string' && payload.message) ||
          t('globalRealtime.deactivatedDefault')
        clearTokens()
        queryClient.clear()
        agentWorkingStore.clear()
        toast.error(reason)
        navigate({ to: '/login' })
        return
      }

      // presence.online / presence.offline mutate the
      // cached `['me','presence']` snapshot in place; `usePresence`
      // hooks across the app re-render off the same cache key. Self-
      // receive is harmless — the Set dedupes (`applyPresenceOnline`
      // is idempotent) and the WS connection IS the user's own online
      // signal, so a frame for the current user is a no-op.
      if (type === 'presence.online' || type === 'presence.offline') {
        const payload =
          typeof message.payload === 'object' && message.payload !== null
            ? (message.payload as Record<string, unknown>)
            : null
        const userId =
          payload && typeof payload.userId === 'string' && payload.userId.length > 0
            ? payload.userId
            : null
        if (userId) {
          if (type === 'presence.online') applyPresenceOnline(queryClient, userId)
          else applyPresenceOffline(queryClient, userId)
        }
        return
      }

      if (type === 'notification.created') {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
        return
      }

      // Code connections / project repositories (A4a): refresh the Integrations
      // list, an open sheet, project repository lists and the card chips.
      if (handleCodeFrame(queryClient, type, message.payload)) return

      // An agent's turn started or finished: its Runs list, any open run's
      // steps and its usage meter are stale. Only the accountable person and
      // `manage-agents` holders can read those routes, but the frame reaches
      // everyone who shares the source, and invalidating a cache nobody
      // observes only marks it stale — no request goes out.
      if (type === 'agent.turn.started' || type === 'agent.turn.finished') {
        const agentUserId =
          typeof message.payload === 'object' && message.payload !== null
            ? (message.payload as Record<string, unknown>).agentUserId
            : null
        if (typeof agentUserId === 'string' && agentUserId.length > 0) {
          invalidateAgentRunSurfaces(queryClient, agentUserId)
        }
        return
      }

      if (type === 'timesheet.approved' || type === 'timesheet.rejected') {
        // Submitter-side fan-out. The approver hits the BE directly
        // and gets fresh data via the mutation's onSuccess. The
        // submitter (us, in this branch) is on a separate session and
        // needs the caches invalidated so the Timesheet page picks up
        // the new lock-state + the entries become editable again on
        // rejection. The Approval queue also refreshes in case the
        // user happens to be both submitter AND approver on different
        // rows.
        queryClient.invalidateQueries({ queryKey: ['timesheets'] })
        queryClient.invalidateQueries({ queryKey: ['time-entries'] })
        queryClient.invalidateQueries({ queryKey: ['approval-queue'] })
        if (type === 'timesheet.rejected') {
          const payload = (message.payload as { reason?: string | null } | null) ?? null
          const reason = payload?.reason ?? null
          toast.info(
            reason
              ? t('globalRealtime.timesheetRejectedWithReason', {
                  defaultValue: 'Your timesheet was rejected: {reason}',
                  reason,
                })
              : t('globalRealtime.timesheetRejected', {
                  defaultValue: 'Your timesheet was rejected. Please edit and resubmit.',
                }),
          )
        } else {
          toast.success(
            t('globalRealtime.timesheetApproved', {
              defaultValue: 'Your timesheet was approved.',
            }),
          )
        }
        return
      }

      if (type === 'timer.started' || type === 'timer.stopped') {
        // Includes the calendar + day-status families: an overlap or cap
        // auto-stop produces a real entry this tab never asked for.
        invalidateTimerDerivedCaches(queryClient)
        if (type === 'timer.stopped') {
          const reason =
            typeof message.payload === 'object' &&
            message.payload !== null &&
            'reason' in (message.payload as Record<string, unknown>)
              ? (message.payload as Record<string, unknown>).reason
              : (message as Record<string, unknown>).reason
          if (reason === 'cap') {
            toast.info(t('globalRealtime.timerCapToast'))
          }
        }
        return
      }

      // Card frames invalidate the master dashboard regardless of the
      // surface that emitted them — a per-project DnD, a per-project
      // PATCH, a tenant-wide announcement create, all should reflect on
      // the master board without a refresh. Mounted in `<Sidebar>` so
      // the master surface picks up these frames even when the user
      // isn't on a per-project board.
      //
      // Per-project boards continue to be handled by `useRealtimeBoard`
      // (which filters by the message's `projectId`); the two listeners
      // are intentional duplicates and TanStack Query coalesces.
      // Tenant-wide announcements broadcast with `projectId: null` —
      // they fan to every tenant member via `broadcastToTenant` and the
      // master listener picks them up here.
      if (
        type === 'card.created' ||
        type === 'card.updated' ||
        type === 'card.moved' ||
        type === 'card.deleted'
      ) {
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: CARD_LIST_QUERY_ROOT })
        // Also invalidate per-project board caches so a tenant-wide
        // announcement frame (`projectId: null`) refreshes any open
        // per-project board the listener doesn't otherwise reach.
        // `useRealtimeBoard` filters by `projectId` and a `null` value
        // is treated as "different project" by its guard, so we cover
        // it here at the global tier.
        queryClient.invalidateQueries({ queryKey: ['board'] })
        // The Timesheet "Available work" rail is membership-derived
        // (assignee / reviewer / collaborator / attendee), so a card frame
        // can add or remove rows for this viewer.
        queryClient.invalidateQueries({ queryKey: AVAILABLE_WORK_QUERY_ROOT })
        // The card's activity log gains a row on every one of these.
        invalidateCardActivityFromFrame(queryClient, message.payload)
        return
      }
      // Comment frames stay project-scoped — handled by `useRealtimeBoard` —
      // except the activity log, which the master surface's card modal also
      // shows and which has no per-project listener there.
      if (type === 'comment.created' || type === 'comment.updated' || type === 'comment.deleted') {
        invalidateCardActivityFromFrame(queryClient, message.payload)
        return
      }

      /*
       * Wiki frames — Phase 1 ships four broadcasts (see plan §5 + the
       * BE realtime helpers at `src/wiki/realtime.ts`). Every frame
       * carries `payload` per `broadcastToTenant`'s shape. Self-dedup
       * for the `draft.updated` toast is mandatory — every save the
       * caller makes also broadcasts a frame, and showing "updated by
       * you" would be obnoxious.
       */
      const payload =
        typeof message.payload === 'object' && message.payload !== null
          ? (message.payload as Record<string, unknown>)
          : null
      const docIdFromPayload =
        payload && typeof payload.docId === 'string' && payload.docId.length > 0
          ? payload.docId
          : null

      if (type === 'wiki.tree.updated') {
        queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
        return
      }

      if (type === 'wiki.doc.published') {
        // Silent live refresh — viewers on the doc get the new content
        // through the invalidation (the body arrives inline on the doc
        // GET); no toast. Publish is holder-only via the advisory
        // mutex, so no open editor can be clobbered by the refetch.
        if (docIdFromPayload) {
          queryClient.invalidateQueries({ queryKey: wikiDocPrefix(docIdFromPayload) })
        }
        queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
        return
      }

      if (type === 'wiki.doc.deleted') {
        if (docIdFromPayload) {
          queryClient.removeQueries({ queryKey: wikiDocPrefix(docIdFromPayload) })
          // If the user is currently viewing the deleted doc, route
          // them back to /wiki with a toast so the empty state takes
          // over rather than the editor erroring on stale state.
          const onThisDoc = isOnWikiDocPath(location.pathname, docIdFromPayload)
          if (onThisDoc) {
            toast.info(t('wiki:doc.deleted'))
            navigate({ to: '/wiki' })
          }
        }
        queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
        return
      }

      if (type === 'wiki.doc.acl.changed') {
        if (docIdFromPayload) {
          // The grant might have just removed the viewer from the doc —
          // refetch meta + body alongside the ACL list so DocPage either
          // re-renders correctly or surfaces the 403 / 404 immediately.
          queryClient.invalidateQueries({ queryKey: wikiDocAclQueryKey(docIdFromPayload) })
          queryClient.invalidateQueries({ queryKey: wikiDocPrefix(docIdFromPayload) })
        }
        // Tenant-wide access knobs can change what's visible in the
        // tree itself (e.g., a folder flipping to `defaultTenantAccess='view'`
        // would surface previously-hidden children).
        queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
        return
      }

      // `wiki.doc.draft.updated` is intentionally NOT handled here.
      // The doc-scoped `useWikiDocRealtime` hook owns it: a viewing
      // tab on the affected doc silently refetches the content
      // (invalidate → body-seed swap); an editing tab skips the
      // refresh so its buffer is never clobbered. The previous
      // "Reload to see latest" toast (no auto-refresh) is gone —
      // toasts are reserved for states that can't be auto-refreshed
      // (e.g. the doc.deleted navigate-away above).
    },
    [queryClient, toast, navigate, t, location.pathname],
  )

  // Auth-failure handling (4401 / 4403) is owned by `<WebSocketProvider>`
  // — it holds `useFreshAccessToken` and refreshes the token on the
  // socket's `onAuthFailure`. This hook only needs to see status + frames.

  const status = useWebSocketSubscription(handleMessage)

  // Refetch notifications on WS reconnect. The
  // `notification.created` frame is fire-and-forget: if the user's tab
  // was offline (laptop closed, network drop) when the frame went out,
  // it's gone. Pairs with `refetchOnWindowFocus: true` in
  // `useNotifications` — together they cover the laptop-wake + tab-
  // refocus paths. We only fire on a TRUE reconnect (open after a prior
  // open), not on the initial connect, so we don't double-fetch on
  // first mount alongside the cache's own initial load.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (status === 'open') {
      if (wasOpenRef.current) {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      } else {
        wasOpenRef.current = true
      }
    }
  }, [status, queryClient])

  return { wsStatus: status }
}
